<template>
  <div>
    <BaseTitle :title="$t(!editable ? 'content.teacher_detail' : edit ? 'content.teacher_edit' : 'content.teacher_create')" />
    <v-row>
      <v-col cols="12" md="auto" style="width: 330px">
        <v-card>
          <div class="pa-8">
            <div v-if="showPhoto">
              <div class="app-section-title">{{ $t('content.user_upload_title') }}</div>
              <form-group :name="$t('form.image')" :validator="$v.form.image" no-gutter>
                <template slot-scope="{ attrs }">
                  <image-box
                    v-model="form.image"
                    v-bind="attrs"
                    :with-upload="editable"
                    :upload-btn-text="$t(edit ? 'change_photo' : 'upload')"
                    class="mt-4"
                    :image-preview="imageUrl"
                  />
                </template>
              </form-group>
            </div>
            <v-btn color="primary" block class="btn-primary-custom" v-if="!showPhoto" :disabled="!editable" @click="togglePhoto">
              {{ $t('content.add_photo') }}
            </v-btn>
          </div>
        </v-card>
        <br />
        <v-card>
          <div class="px-8 py-4">
            <form-group :name="$t('form.status')" :validator="$v.form.status">
              <template slot-scope="{ attrs }">
                <v-select :items="status" :label="$t('form.status')" :disabled="!editable" v-bind="attrs" v-model="form.status" width="100">
                  <template #item="{item}">
                    {{ $t(item.text) }}
                  </template>
                  <template #selection="{item}">
                    {{ $t(item.text) }}
                  </template>
                </v-select>
              </template>
            </form-group>
          </div>
        </v-card>
      </v-col>
      <v-col>
        <form @submit.prevent="submit">
          <v-card class="mb-8">
            <div class="pa-8">
              <div class="app-section-title">
                {{ $t('content.personal_information') }}
              </div>
              <form-group :name="$t('form.first_name')" :validator="$v.form.firstName">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.firstName"
                    v-bind="attrs"
                    :disabled="!editable"
                    @input="$v.form.firstName.$touch()"
                    @blur="$v.form.firstName.$touch()"
                  >
                    <template #label>
                      {{ $t('form.first_name') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
              </form-group>
              <form-group :name="$t('form.last_name')" :validator="$v.form.lastName">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.lastName"
                    v-bind="attrs"
                    :disabled="!editable"
                    :label="$t('form.last_name')"
                    @input="$v.form.lastName.$touch()"
                    @blur="$v.form.lastName.$touch()"
                  />
                </template>
              </form-group>
              <form-group :name="$t('form.identityNumber')" :validator="$v.form.identityNumber">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.identityNumber"
                    v-bind="attrs"
                    :disabled="!editable"
                    :label="$t('form.identityNumber')"
                    @input="$v.form.identityNumber.$touch()"
                    @blur="$v.form.identityNumber.$touch()"
                  />
                </template>
              </form-group>
              <form-group :name="$t('form.gender')" :validator="$v.form.gender">
                <template slot-scope="{ attrs }">
                  <v-select :items="gender" :disabled="!editable" v-bind="attrs" v-model="form.gender">
                    <template #label>
                      {{ $t('form.gender') }}
                      <span class="error--text">*</span>
                    </template>
                    <template #item="{item}">
                      {{ $t(item.text) }}
                    </template>
                    <template #selection="{item}">
                      {{ $t(item.text) }}
                    </template>
                  </v-select>
                </template>
              </form-group>
              <form-group :name="$t('form.place_of_birth')" :validator="$v.form.placeOfBirth">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.placeOfBirth"
                    v-bind="attrs"
                    :disabled="!editable"
                    @input="$v.form.placeOfBirth.$touch()"
                    @blur="$v.form.placeOfBirth.$touch()"
                  >
                    <template #label>
                      {{ $t('form.place_of_birth') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
              </form-group>
              <form-group v-slot="{ attrs, on }" :name="$t('form.date_of_birth')" :validator="$v.form.dateOfBirth">
                <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                    <v-text-field
                      v-model="form.dateOfBirth"
                      :disabled="!editable"
                      readonly
                      v-bind="{ ...attrs, ...menuAttrs }"
                      v-on="{ ...on, ...menuOn }"
                      @blur="$v.form.dateOfBirth.$touch()"
                    >
                      <template #label>
                        {{ $t('form.date_of_birth') }}
                        <span class="error--text">*</span>
                      </template>
                      <template #append> <svg-icon icon="calendar" class="primary--text mr-3" /> </template>
                    </v-text-field>
                  </template>
                  <v-date-picker v-model="form.dateOfBirth" :active-picker.sync="activePicker" :max="datemaxcurrent" :locale="$i18n.locale">
                    <template #label>
                      {{ $t('form.date_of_birth') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-date-picker>
                </v-menu>
              </form-group>
              <form-group :name="$t('form.email')" :validator="$v.form.email">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.email"
                    v-bind="attrs"
                    :disabled="!editable"
                    :label="$t('form.email')"
                    @input="$v.form.email.$touch()"
                    @blur="$v.form.email.$touch()"
                  >
                    <template #label>
                      {{ $t('form.email') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
              </form-group>
              <form-group :name="$t('form.phone_number')" :validator="$v.form.phoneNumber">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.phoneNumber"
                    v-bind="attrs"
                    v-phone
                    :disabled="!editable"
                    :label="$t('form.phone_number')"
                    @input="$v.form.phoneNumber.$touch()"
                    @blur="$v.form.phoneNumber.$touch()"
                  >
                    <template #label>
                      {{ $t('form.phone_number') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
              </form-group>

              <div class="app-section-title">{{ $t('content.address_information') }}</div>
              <form-group :name="$t('form.address')" :validator="$v.form.address">
                <template slot-scope="{ attrs }">
                  <v-textarea
                    v-model="form.address"
                    v-bind="attrs"
                    :disabled="!editable"
                    @input="$v.form.address.$touch()"
                    @blur="$v.form.address.$touch()"
                  >
                    <template #label>
                      {{ $t('form.address') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-textarea>
                </template>
              </form-group>
              <form-group :name="$t('form.province')" :validator="$v.form.provinceId">
                <template slot-scope="{ attrs }">
                  <v-autocomplete
                    :items="provinces"
                    :loading="loadingProvinces"
                    :disabled="!editable"
                    item-value="provinceId"
                    item-text="name"
                    v-bind="attrs"
                    v-model="form.provinceId"
                    @change="getCities"
                  >
                    <template #label>
                      {{ $t('province') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-autocomplete>
                </template>
              </form-group>
              <form-group :name="$t('form.city')" :validator="$v.form.cityId">
                <template slot-scope="{ attrs }">
                  <v-autocomplete
                    :items="cities"
                    :loading="loadingCities"
                    :disabled="!editable"
                    item-value="cityId"
                    item-text="name"
                    v-bind="attrs"
                    v-model="form.cityId"
                  >
                    <template #label>
                      {{ $t('form.city') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-autocomplete>
                </template>
              </form-group>
              <form-group :name="$t('form.postal_code')" :validator="$v.form.postalCode">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    minlength="5"
                    maxlength="5"
                    v-model.number="form.postalCode"
                    v-bind="attrs"
                    :disabled="!editable"
                    v-number
                    :label="$t('form.postal_code')"
                    @input="$v.form.postalCode.$touch()"
                    @blur="$v.form.postalCode.$touch()"
                  />
                </template>
              </form-group>
            </div>
          </v-card>
          <div class="d-flex justify-end align-center">
            <v-btn color="primary" class="btn-primary-custom mr-3" outlined @click="backToList">{{ $t(edit ? 'back' : 'cancel') }}</v-btn>
            <v-btn v-if="editable" color="primary" :loading="loading" class="btn-primary-custom" type="submit">{{
              $t(edit ? 'save' : 'submit')
            }}</v-btn>
          </div>
        </form>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, email, numeric, maxLength } from 'vuelidate/lib/validators';
import phoneNumber from '@/validators/phoneNumber';
import { fileSize, alphaSpace } from '@/validators';
import BaseTitle from '@/components/base/Title';
import ImageBox from '@/components/ImageBox';

export default {
  name: 'MasterUserCreateEdit',
  components: {
    BaseTitle,
    ImageBox
  },
  props: {
    editable: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters({
      status: 'Reference/itemStatus',
      provinces: 'Reference/itemProvinces',
      gender: 'Reference/itemGender',
      user: 'Auth/user'
    }),
    cities: {
      get() {
        return this.$store.getters['Reference/itemCities']();
      }
    }
  },
  watch: {
    user: {
      deep: true,
      immediate: true,
      handler: function(value) {
        this.form.schoolId = value.schoolId;
      }
    },
    menu(val) {
      val && setTimeout(() => (this.activePicker = 'YEAR'));
    }
  },
  validations: function() {
    return {
      form: {
        image: { fileSize: fileSize(1) },
        firstName: { required, alphaSpace, maxLength: maxLength(15) },
        lastName: { alphaSpace, maxLength: maxLength(15) },
        phoneNumber: { required, phone: phoneNumber, maxLength: maxLength(15) },
        email: {
          required,
          email,
          [this.validationName](value) {
            if (!value) return true;
            return new Promise(resolve => {
              this.axios
                .get('api/user/validate-email', {
                  params: {
                    email: value,
                    roleId: 4,
                    userId: this.initialData?.userId || this.$route.params?.id || null
                  }
                })
                .then(() => resolve(true))
                .catch(err => {
                  const code = err?.response?.data?.errorMessages;
                  const errorCode = ['isRegistered', 'isRegisteredInactive', 'isRegisteredInAnotherRole'];
                  this.validationName = errorCode?.[code - 1];
                  resolve(false);
                });
            });
          }
        },
        gender: { required },
        address: { required, maxAddressLength: maxLength(255) },
        placeOfBirth: { required, alphaSpace },
        dateOfBirth: {
          required,
          date: function(value) {
            return this.datemaxcurrent ? value <= this.datemaxcurrent : true;
          }
        },
        status: { required },
        provinceId: { required },
        cityId: { required },
        identityNumber: { numeric, maxLength: maxLength(15) },
        postalCode: { numeric, maxLength: maxLength(5) }
      }
    };
  },
  data: () => ({
    form: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      gender: '',
      address: '',
      status: true,
      identityNumber: '',
      placeOfBirth: '',
      provinceId: null,
      dateOfBirth: '',
      cityId: '',
      postalCode: '',
      roleId: 4,
      schoolId: '',
      image: null,
      used: ''
    },
    edit: false,
    validationName: 'dynamic',
    showPhoto: false,
    datemaxcurrent: (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 17);
      return d.toISOString().split('T')[0];
    })(),
    imageUrl: null,
    menu: false,
    loading: false,
    hasLoad: false,
    loadingProvinces: false,
    loadingCities: false,
    activePicker: null
  }),
  methods: {
    getData() {
      this.$store.dispatch('Teacher/fetchDetail', this.$route.params.id).then(res => {
        // Object.keys(this.form).forEach(form => (this.form[form] = res[form]));
        Object.keys(this.form).forEach(form => {
          this.form[form] = res[form];
        });

        this.form.image = null;
        if (res.imageUrl) {
          this.imageUrl = res.imageUrl;
          this.showPhoto = true;
        }
        this.getCities();
        this.hasLoad = true;
      });
    },
    getProvinces() {
      this.loadingProvinces = true;
      this.$store.dispatch('Reference/fetchProvinces').finally(() => (this.loadingProvinces = false));
    },
    getCities() {
      if (this.hasLoad) {
        this.form.cityId = null;
      }
      this.loadingCities = true;
      this.$store.dispatch('Reference/fetchCities', this.form.provinceId).finally(() => (this.loadingCities = false));
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      }

      this.loading = true;
      const payload = { ...(this.edit ? { id: this.$route.params.id } : null), ...this.form };
      this.$store
        .dispatch(`Teacher/${this.edit ? 'update' : 'save'}`, payload)
        .then(() => this.$router.push({ name: this.edit ? 'TeacherUpdateSuccess' : 'TeacherSuccess' }))
        .finally(() => (this.loading = false));
    },
    backToList() {
      var backuser = localStorage.getItem('backUser');
      if (backuser) {
        localStorage.removeItem('backUser');
        this.$router.push({ name: 'MasterUserSchool' });
      } else {
        this.$router.push({ name: 'TeacherIndex' });
      }
    },
    preventNav(event) {
      if (!this.edit || !this.$v.$anyDirty) return;
      event.preventDefault();
      event.returnValue = '';
    },
    togglePhoto() {
      this.showPhoto = !this.showPhoto;
    }
  },
  created() {
    this.$store.commit('Reference/RESET_CITIES');
    this.getProvinces();

    if (this.$route.params.id) {
      this.edit = true;
      this.getData();
    } else {
      this.hasLoad = true;
    }
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('beforeunload', this.preventNav));
  },
  beforeRouteLeave(to, from, next) {
    if (!this.edit && this.$v.$anyDirty && this.$v.$invalid) {
      if (!window.confirm(this.$t('alert.leave_message'))) {
        return;
      }
    }
    next();
  }
};
</script>
f
